import * as React from 'react';
import {
  isDefined,
  noop,
  isHoverForInteractiveType,
  saveNodeType,
  terminate,
  getValueFromOverride,
} from 'react-financial-charts';
import { HoverTextNearMouse, MouseLocationIndicator } from '@react-financial-charts/interactive/lib/components/index';
import { EachRectangle } from './Wrapper';
import type { TrendLineProps } from './TrendLine';
import type { TrendLineDragData } from './Wrapper/EachTrendLine';
import type { CoordDragOverride, RectangleRenderNode, RenderNode, MoreProps } from '../../../types/commontypes';
import { TrendLine } from './TrendLine';
import { chartOrder } from '@/utils/chartConfig';
import { clampYToChart, ShapeType } from '@/index';
import { toRectangleRFCProps } from '@/utils/shapePropsToAppearance';
import { getActiveTemplateNode } from '@/utils/useActiveShapeProps';
import { defaultShapePropertyValues } from '@/utils/defaultShapePropertyValues';
import { ChartShapesContext } from '../../../contexts/ChartShapesContext';

interface RectangleState {
  override?: CoordDragOverride;
  trends?: RenderNode | RenderNode[] | null;
}

export class Rectangle extends React.Component<TrendLineProps, RectangleState> {
  public static contextType = ChartShapesContext;
  declare context: React.ContextType<typeof ChartShapesContext>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static _cachedDefaultProps: any = null;
  static get defaultProps() {
    if (!Rectangle._cachedDefaultProps) {
      Rectangle._cachedDefaultProps = {
        onStart: noop,
        onSelect: noop,
        currentPositionStroke: '#000000',
        currentPositionstrokeOpacity: 1,
        currentPositionStrokeWidth: 3,
        currentPositionRadius: 0,
        shouldDisableSnap: (e: React.MouseEvent) => e.button === 2 || e.shiftKey,
        hoverText: {
          ...HoverTextNearMouse.defaultProps,
          enable: true,
          bgHeight: 'auto',
          bgWidth: 'auto',
          text: 'Click to select object',
          selectedText: '',
        },
        trends: [],
      };
    }
    return Rectangle._cachedDefaultProps;
  }

  // @ts-ignore
  private getSelectionState: (...args: unknown[]) => unknown;
  private mouseMoved = false;
  private saveNodeType: <T>(idx: number) => React.Ref<T>;
  // @ts-ignore
  private terminate: (...args: unknown[]) => unknown;

  public constructor(props: TrendLineProps) {
    super(props);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState = isHoverForInteractiveType('trends').bind(this);

    this.state = {};
  }

  public render() {
    const {
      currentPositionstrokeOpacity,
      currentPositionRadius = TrendLine.defaultProps.currentPositionRadius,
      currentPositionStroke,
      currentPositionStrokeWidth,
      enabled,
      hoverText,
      shouldDisableSnap,
      snap,
      snapTo,
      trends,
      onDoubleClick,
      sharedState,
      chartId,
      isAnyShapeEnabled,
    } = this.props;
    const { override } = this.state;
    const tempCondition =
      sharedState?.type === ShapeType.RECTANGLE &&
      chartId === sharedState?.chartId &&
      sharedState?.endXY &&
      sharedState?.status !== 'completed';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const activeNode = getActiveTemplateNode(ShapeType.RECTANGLE) as RectangleRenderNode;
    const activeAppearance = toRectangleRFCProps(activeNode);
    const tempLine = tempCondition ? (
      <EachRectangle
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        type={activeAppearance.type as 'XLINE' | 'RAY' | 'LINE'}
        uuid={''}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        x1Value={(sharedState?.startXY as [number, number])?.[0]}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        y1Value={(sharedState?.startXY as [number, number])?.[1]}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        x2Value={(sharedState?.endXY as [number, number])?.[0]}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        y2Value={(sharedState?.endXY as [number, number])?.[1]}
        strokeStyle={activeAppearance.strokeStyle}
        strokeWidth={activeAppearance.strokeWidth}
        selected={true}
        strokeDasharray={activeAppearance.strokeDasharray}
        edgeStroke={activeAppearance.edgeStroke}
        edgeFill={activeAppearance.edgeFill}
        edgeStrokeWidth={activeAppearance.edgeStrokeWidth}
        r={activeAppearance.r}
        onDrag={this.handleDragLine}
        onDragComplete={this.handleDragLineComplete}
        edgeInteractiveCursor="big-declined-arrow-cursor"
        lineInteractiveCursor="react-financial-charts-pointer-cursor"
        strokeOpacity={0}
        extendLeft={activeAppearance.extendLeft}
        extendRight={activeAppearance.extendRight}
        arrowDirection={'none'}
        locked={false}
        fillStyle={activeAppearance.fillStyle}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        middle={activeAppearance.middle as React.ComponentProps<typeof EachRectangle>['middle']}
        onDoubleClick={onDoubleClick}
        leftArrow={false}
        rightArrow={false}
        bgStyle={undefined}
        textColor={undefined}
        sharedState={sharedState ?? {}}
      />
    ) : null;
    return (
      <g>
        {trends.map((each, idx) => {
          if (each.hidden) return null;
          if (each.type !== ShapeType.RECTANGLE) return null;
          const eachAppearance = toRectangleRFCProps(each);
          const eachAppearanceAny = eachAppearance as Record<string, unknown>;
          const hoverTextWithDefault = {
            ...TrendLine.defaultProps.hoverText,
            ...hoverText,
          };
          const eachStartEnd = each;
          return (
            <EachRectangle
              key={idx}
              uuid={each.uuid}
              ref={this.saveNodeType(idx)}
              index={idx}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              type={eachAppearance.type as 'XLINE' | 'RAY' | 'LINE'}
              selected={(each as unknown as { selected?: boolean }).selected}
              x1Value={getValueFromOverride(override, each.uuid, 'x1Value', eachStartEnd.start?.[0])}
              y1Value={getValueFromOverride(override, each.uuid, 'y1Value', eachStartEnd.start?.[1])}
              x2Value={getValueFromOverride(override, each.uuid, 'x2Value', eachStartEnd.end?.[0])}
              y2Value={getValueFromOverride(override, each.uuid, 'y2Value', eachStartEnd.end?.[1])}
              strokeStyle={eachAppearance.strokeStyle}
              strokeWidth={eachAppearance.strokeWidth}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              strokeOpacity={eachAppearanceAny.strokeOpacity as number}
              strokeDasharray={eachAppearance.strokeDasharray}
              edgeStroke={eachAppearance.edgeStroke}
              edgeFill={eachAppearance.edgeFill}
              edgeStrokeWidth={eachAppearance.edgeStrokeWidth}
              r={eachAppearance.r}
              hoverText={hoverTextWithDefault}
              onDrag={this.handleDragLine}
              onDragComplete={this.handleDragLineComplete}
              edgeInteractiveCursor="big-declined-arrow-cursor"
              lineInteractiveCursor="react-financial-charts-move-cursor"
              onSelect={this.handleSelect}
              onHover={this.handleHover}
              locked={each.locked ?? false}
              onDoubleClick={onDoubleClick}
              extendLeft={eachAppearance.extendLeft}
              extendRight={eachAppearance.extendRight}
              arrowDirection={'none'}
              fillStyle={eachAppearance.fillStyle}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              middle={eachAppearance.middle as React.ComponentProps<typeof EachRectangle>['middle']}
              leftArrow={false}
              rightArrow={false}
              chartId={each.chartId}
              sharedState={sharedState ?? {}}
              bgStyle={undefined}
              textColor={undefined}
              isAnyShapeEnabled={isAnyShapeEnabled}
            />
          );
        })}
        {tempLine}
        <MouseLocationIndicator
          enabled={enabled}
          snap={snap}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          opacity={currentPositionstrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          // onClick={this.handleEnd}
          onMouseMove={this.handleDrawLine}
        />
      </g>
    );
  }

  private readonly handleHover = (hoveredId: string | number | null, moreProps?: MoreProps) => {
    const { onHover } = this.props;
    if (onHover) {
      onHover(hoveredId, moreProps);
    }
  };

  private readonly handleSelect = (e: React.MouseEvent, selectedUuid: string, moreProps: MoreProps) => {
    const { trends } = this.props;
    const newTrends = trends.map((trends, _idx) => ({
      ...trends,
      selected: trends.uuid === selectedUuid,
    }));

    const { onSelect } = this.props;
    if (onSelect !== undefined) {
      onSelect(e, newTrends, moreProps);
    }
  };

  private readonly handleStart = (e: React.MouseEvent, xyValue: number[], moreProps: MoreProps) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const xy = xyValue as [number, number];
    const { sharedState, enabled } = this.props;
    if (!enabled) return;
    if (sharedState?.status !== 'started') {
      this.mouseMoved = false;
      const { onStart } = this.props;
      if (onStart !== undefined) {
        onStart(e, xy, moreProps);
      }
    } else {
      const shapesChartId = sharedState?.chartId;
      // Only do min/max clamp if sharedState exists
      const yValue = clampYToChart(moreProps.chartId, shapesChartId, moreProps.chartConfigs, chartOrder, xy[1]);
      this.handleEnd(e, [xy[0], yValue], moreProps);
    }
  };

  private readonly handleEnd = (e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => {
    const { trends, sharedState, enabled } = this.props;
    const chartId = moreProps.chartId;
    if (!enabled) return;
    if (sharedState?.status === 'started' && sharedState?.startXY) {
      const targetChartId = sharedState?.chartId || chartId;
      const shapeUuid = crypto.randomUUID();
      const lastProps = getActiveTemplateNode(ShapeType.RECTANGLE);
      const defaults = defaultShapePropertyValues[ShapeType.RECTANGLE];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const initial = { ...defaults, ...lastProps } as Partial<RectangleRenderNode>;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const newTrends = [
        ...trends.map(d => ({ ...d, selected: false })),
        {
          ...initial,
          type: ShapeType.RECTANGLE,
          start: sharedState?.startXY,
          end: xyValue,
          selected: true,
          hidden: false,
          uuid: shapeUuid,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          chartId: targetChartId,
        },
      ] as RenderNode[];
      this.setState(
        {
          trends: newTrends,
        },
        () => {
          const { onComplete } = this.props;
          if (onComplete !== undefined) {
            onComplete(e, newTrends, moreProps);
          }
        },
      );
    }
  };

  private readonly handleDrawLine = (_: React.MouseEvent, xyValue: number[], moreProps: MoreProps) => {
    const { setSharedState, sharedState, chartId, enabled } = this.props;
    if (!sharedState || sharedState.status !== 'started' || !enabled) return;

    const startChartId = sharedState.chartId;
    const pointerChartId = moreProps.currentCharts?.[0];

    // Only draw for shapes created on this chart
    if (chartId !== startChartId) return;

    this.mouseMoved = true;
    const [x] = xyValue;
    let [, y] = xyValue;

    // Clamp Y if pointer is on a different chart
    y = clampYToChart(pointerChartId, startChartId, moreProps.chartConfigs, chartOrder, y);

    setSharedState?.({
      ...sharedState,
      endXY: [x, y],
    });
  };

  private readonly handleDragLineComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { override } = this.state;
    if (isDefined(override)) {
      const { trends } = this.props;
      const { x1Value, x2Value, y1Value, y2Value } = override;
      if (!isDefined(x1Value) || !isDefined(y1Value) || !isDefined(x2Value) || !isDefined(y2Value)) {
        return;
      }

      const dragUuid = override.uuid as string | undefined;
      const newTrends = trends.map((each, _idx) =>
        each.uuid === dragUuid
          ? {
              ...each,
              start: [x1Value, y1Value],
              end: [x2Value, y2Value],
              selected: true,
              updatedAt: Date.now(),
            }
          : {
              ...each,
              selected: false,
            },
      ) as RenderNode[];

      const { onDragComplete } = this.props;
      onDragComplete?.(e, newTrends, moreProps);
      this.setState({ override: undefined });
    }
  };

  private readonly handleDragLine = (_: React.MouseEvent, uuid: string | undefined, newXYValue: TrendLineDragData) => {
    const { hoverId } = this.props;
    const { override } = this.state;
    if (uuid === undefined) return;
    if (override) {
      if (override.uuid !== uuid || override.uuid !== hoverId) {
        return;
      }
    }
    this.mouseMoved = true;
    this.setState({
      override: {
        uuid,
        ...newXYValue,
      },
    });
  };
}
